import {
  ACTION_DESKTOP_NAV_MENU_DESTINATION,
  ACTION_MOBILE_NAV_MENU_DESTINATION,
  CATEGORY_NAV,
} from 'utils/analytics/eventTypesConstants';
import {
  ACTION_GO_TO_LOGIN,
  ACTION_GO_TO_SIGNUP,
} from 'utils/analytics/eventTypeConstantsAmplitude';
import { AvatarImage, Button, List, ListItem } from 'omakase';
import { IconArrowRightSmall } from 'ui/icons';
import { IconChevronSmallRight } from '@tock/icons';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import { Theme, useTheme } from '@emotion/react';
import { computeLogInHref, computeSignupHref } from '../../utils/logInSignUpHrefUtils';
import {
  computeSiteMenuTopLevelHeadingButtonsContainer,
  siteMenuTopLevelHeadingContainer,
  siteMenuTopLevelHeadingContainerLoggedIn,
} from './siteMenuTopLevelHeadingCssDefs';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import { useMediaQuery } from '@material-ui/core';
import Anchor from 'components/Anchor';
import React from 'react';

export const DEFAULT_PROFILE_IMAGE_URL =
  'https://storage.googleapis.com/tock-public-assets/storybook/default-avatar.png';

export interface SiteMenuTopLevelHeadingProps {
  /**
   * Link to redirect browser to when "Profile" button (logged-in state) is clicked
   */
  href: string;
  /**
   * Current route
   */
  pathname: string;
  /**
   * Whether or not the component is rendering when a user is logged in
   */
  loggedIn: boolean;
  /**
   * Callback for closing the menu
   */
  onClick(): void;
  /**
   * Obtain patron information (for now...just 'name') from Redux state -> props
   */
  patronName?: string;
  /**
   * Link to patron profile image
   */
  profileImageLink?: string;
}

const SiteMenuTopLevelHeading: React.FunctionComponent<SiteMenuTopLevelHeadingProps> = ({
  href,
  pathname,
  loggedIn,
  onClick,
  patronName,
  profileImageLink = DEFAULT_PROFILE_IMAGE_URL,
  ...rest
}) => {
  const amplitudeSendEvent = useAmplitudeSendEvent();
  const theme: Theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const rootProps = {
    css: (theme: Theme) => [
      siteMenuTopLevelHeadingContainer(theme),
      loggedIn && siteMenuTopLevelHeadingContainerLoggedIn(theme),
    ],
    ...rest,
  };

  const loggedInInner = (
    <List>
      <ListItem
        component={Anchor}
        elementStart={<AvatarImage size="large" alt="profile photo" src={profileImageLink} />}
        elementEndProps={{
          element: (
            <IconMigrationUtil
              legacyIcon={<IconArrowRightSmall />}
              newIcon={<IconChevronSmallRight />}
            />
          ),
        }}
        href={href}
        onClick={() => {
          onClick();
          window.analytics?.sendEvent(
            CATEGORY_NAV,
            isDesktop ? ACTION_DESKTOP_NAV_MENU_DESTINATION : ACTION_MOBILE_NAV_MENU_DESTINATION,
            'View profile'
          );
        }}
        key="site-menu-profile-link"
        secondaryText="View profile"
        variant="basic"
        size="large"
        weight="medium"
      >
        Hi, {patronName}
      </ListItem>
    </List>
  );

  const loggedOutInner = (
    <div css={computeSiteMenuTopLevelHeadingButtonsContainer}>
      <Button
        variant="primary"
        component={Anchor}
        href={computeSignupHref(pathname)}
        onClick={() => {
          amplitudeSendEvent(ACTION_GO_TO_SIGNUP);
          onClick();
          window.analytics?.sendEvent(CATEGORY_NAV, ACTION_MOBILE_NAV_MENU_DESTINATION, 'Sign up');
        }}
        size="medium"
        data-testid="sign-up-link"
      >
        Sign up
      </Button>
      <Button
        variant="basic"
        component={Anchor}
        href={computeLogInHref(pathname)}
        onClick={() => {
          amplitudeSendEvent(ACTION_GO_TO_LOGIN);
          onClick();
          window.analytics?.sendEvent(CATEGORY_NAV, ACTION_MOBILE_NAV_MENU_DESTINATION, 'Log in');
        }}
        size="medium"
        data-testid="log-in-link"
      >
        Log in
      </Button>
    </div>
  );
  return <div {...rootProps}>{loggedIn ? loggedInInner : loggedOutInner}</div>;
};

export default SiteMenuTopLevelHeading;
